import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Loader2,
  Plus,
  Save,
  Trash2,
  RotateCw,
  Layers,
  X,
  Check,
  Download,
  Grid3x3,
  FileText,
  Share2,
  Type,
  Link2,
  ChevronDown,
  Library,
  Upload,
  Shapes,
  Square,
  Circle,
  Triangle,
  Minus,
  Palette,
  SlidersHorizontal,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  fetchMoodboard,
  updateMoodboard,
  fetchProjectImages,
  fetchProject,
  uploadImage,
  analyzeImage,
  importFromUrl,
} from "@/api";
import ExportChecklistDialog from "@/components/ExportChecklistDialog";
import ShareDialog from "@/components/ShareDialog";
import PdfThumbnail from "@/components/PdfThumbnail";
import MoodboardTextStyleDialog, {
  FONT_FAMILIES,
} from "@/components/MoodboardTextStyleDialog";
import MoodboardShapeStyleDialog from "@/components/MoodboardShapeStyleDialog";
import MoodboardShape from "@/components/MoodboardShape";
import { readFileAsDataURL, downsizeImage, isPdf } from "@/lib/utils";
import { useConfirm } from "@/components/ConfirmDialog";

const CANVAS_W = 1600;
const CANVAS_H = 1000;
const GRID = 40; // snap-to-grid size in canvas units
const BG_SWATCHES = [
  "#F7F7F5",
  "#EFE9DF",
  "#E8E4DC",
  "#1C1B17",
  "#2A2E2A",
  "#C27A62",
  "#8A9A86",
];

function extractUrlsFromDT(dt) {
  const urls = new Set();
  const uri = dt.getData("text/uri-list");
  if (uri) {
    uri
      .split(/\r?\n/)
      .filter((l) => l && !l.startsWith("#"))
      .forEach((l) => urls.add(l.trim()));
  }
  const plain = dt.getData("text/plain") || "";
  plain
    .split(/\s+/)
    .filter((s) => /^https?:\/\//i.test(s))
    .forEach((s) => urls.add(s));
  const html = dt.getData("text/html") || "";
  const re = /<img[^>]+src=["']([^"']+)["']/gi;
  let m;
  while ((m = re.exec(html))) {
    if (m[1].startsWith("http")) urls.add(m[1]);
  }
  return Array.from(urls);
}

export default function MoodboardEditor() {
  const { projectId, moodboardId } = useParams();
  const nav = useNavigate();
  const confirm = useConfirm();
  const [project, setProject] = useState(null);
  const [moodboard, setMoodboard] = useState(null);
  const [images, setImages] = useState([]);
  const [items, setItems] = useState([]);
  const [background, setBackground] = useState("#F7F7F5");
  const [name, setName] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [snapEnabled, setSnapEnabled] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [pdfDialogOpen, setPdfDialogOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [editingTextK, setEditingTextK] = useState(null);
  const [textStyleOpen, setTextStyleOpen] = useState(false);
  const [shapeStyleOpen, setShapeStyleOpen] = useState(false);

  // If the user selects a different item, exit inline edit mode.
  useEffect(() => {
    if (editingTextK && selectedId !== editingTextK) setEditingTextK(null);
  }, [selectedId, editingTextK]);
  const [urlImportOpen, setUrlImportOpen] = useState(false);
  const [urlValue, setUrlValue] = useState("");
  const [urlBusy, setUrlBusy] = useState(false);
  const [dropOver, setDropOver] = useState(false);
  const [dropBusy, setDropBusy] = useState(false);
  const dropCounter = useRef(0);
  const deviceInputRef = useRef(null);

  const canvasRef = useRef(null);
  const dragRef = useRef(null);
  const shiftRef = useRef(false);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const [p, mb, imgs] = await Promise.all([
          fetchProject(projectId),
          fetchMoodboard(moodboardId),
          fetchProjectImages(projectId),
        ]);
        if (!active) return;
        setProject(p);
        setMoodboard(mb);
        setItems(
          (mb.items || []).map((it, i) => ({
            kind: "image",
            ...it,
            _k: `k-${i}`,
          }))
        );
        setName(mb.name);
        setBackground(mb.background || "#F7F7F5");
        setImages(imgs);
      } catch {
        toast.error("Could not load moodboard");
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [projectId, moodboardId]);

  // Warn if leaving with unsaved changes
  useEffect(() => {
    function beforeUnload(e) {
      if (dirty) {
        e.preventDefault();
        e.returnValue = "";
      }
    }
    window.addEventListener("beforeunload", beforeUnload);
    return () => window.removeEventListener("beforeunload", beforeUnload);
  }, [dirty]);

  // Track Shift to bypass snap-to-grid temporarily
  useEffect(() => {
    function down(e) {
      if (e.key === "Shift") shiftRef.current = true;
    }
    function up(e) {
      if (e.key === "Shift") shiftRef.current = false;
    }
    window.addEventListener("keydown", down);
    window.addEventListener("keyup", up);
    return () => {
      window.removeEventListener("keydown", down);
      window.removeEventListener("keyup", up);
    };
  }, []);

  const imageById = useMemo(
    () => Object.fromEntries(images.map((i) => [i.id, i])),
    [images]
  );

  const markDirty = useCallback(() => setDirty(true), []);

  function snap(v) {
    if (!snapEnabled || shiftRef.current) return v;
    return Math.round(v / GRID) * GRID;
  }

  function addToCanvas(image, pos = null) {
    const k = `k-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
    const w = 280;
    const h = 280;
    const x = pos
      ? pos.x - w / 2
      : CANVAS_W / 2 - w / 2 + (Math.random() - 0.5) * 200;
    const y = pos
      ? pos.y - h / 2
      : CANVAS_H / 2 - h / 2 + (Math.random() - 0.5) * 200;
    const maxZ = items.reduce((m, i) => Math.max(m, i.z || 1), 0);
    setItems((prev) => [
      ...prev,
      {
        _k: k,
        kind: "image",
        image_id: image.id,
        x: snap(x),
        y: snap(y),
        w,
        h,
        rotation: 0,
        z: maxZ + 1,
      },
    ]);
    setSelectedId(k);
    markDirty();
  }

  function addTextBlock(pos = null) {
    const k = `k-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
    const w = 420;
    const h = 120;
    const x = pos
      ? pos.x - w / 2
      : CANVAS_W / 2 - w / 2 + (Math.random() - 0.5) * 200;
    const y = pos
      ? pos.y - h / 2
      : CANVAS_H / 2 - h / 2 + (Math.random() - 0.5) * 200;
    const maxZ = items.reduce((m, i) => Math.max(m, i.z || 1), 0);
    setItems((prev) => [
      ...prev,
      {
        _k: k,
        kind: "text",
        text: "Double-click to edit",
        font_family: "display",
        font_size: 42,
        color: "#1c1b17",
        align: "left",
        x: snap(x),
        y: snap(y),
        w,
        h,
        rotation: 0,
        z: maxZ + 1,
      },
    ]);
    setSelectedId(k);
    markDirty();
  }

  function addShape(shape, pos = null) {
    const k = `k-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
    let w = 320;
    let h = 320;
    if (shape === "line") {
      w = 480;
      h = 20;
    } else if (shape === "triangle") {
      w = 280;
      h = 240;
    }
    const x = pos
      ? pos.x - w / 2
      : CANVAS_W / 2 - w / 2 + (Math.random() - 0.5) * 200;
    const y = pos
      ? pos.y - h / 2
      : CANVAS_H / 2 - h / 2 + (Math.random() - 0.5) * 200;
    const maxZ = items.reduce((m, i) => Math.max(m, i.z || 1), 0);
    setItems((prev) => [
      ...prev,
      {
        _k: k,
        kind: "shape",
        shape,
        fill: shape === "line" ? "#1c1b17" : "#C27A62",
        stroke: "#1c1b17",
        stroke_width: shape === "line" ? 4 : 0,
        corner_radius: shape === "rectangle" ? 8 : 0,
        opacity: 1,
        x: snap(x),
        y: snap(y),
        w,
        h,
        rotation: 0,
        z: maxZ + 1,
      },
    ]);
    setSelectedId(k);
    markDirty();
  }

  function updateSelectedItem(patch) {
    if (!selectedId) return;
    setItems((prev) =>
      prev.map((i) => (i._k === selectedId ? { ...i, ...patch } : i))
    );
    markDirty();
  }

  const selectedItem = items.find((i) => i._k === selectedId) || null;

  function removeSelected() {
    if (!selectedId) return;
    setItems((prev) => prev.filter((i) => i._k !== selectedId));
    setSelectedId(null);
    markDirty();
  }

  function rotateSelected(delta) {
    if (!selectedId) return;
    setItems((prev) =>
      prev.map((i) =>
        i._k === selectedId
          ? { ...i, rotation: (i.rotation || 0) + delta }
          : i
      )
    );
    markDirty();
  }

  function bringForward() {
    if (!selectedId) return;
    setItems((prev) => {
      const maxZ = prev.reduce((m, i) => Math.max(m, i.z || 1), 0);
      return prev.map((i) =>
        i._k === selectedId ? { ...i, z: maxZ + 1 } : i
      );
    });
    markDirty();
  }

  function sendBackward() {
    if (!selectedId) return;
    setItems((prev) => {
      const minZ = prev.reduce(
        (m, i) => Math.min(m, i.z || 1),
        Number.POSITIVE_INFINITY
      );
      return prev.map((i) =>
        i._k === selectedId ? { ...i, z: Math.max(1, minZ - 1) } : i
      );
    });
    markDirty();
  }

  function getCanvasPoint(e) {
    const rect = canvasRef.current.getBoundingClientRect();
    const scaleX = CANVAS_W / rect.width;
    const scaleY = CANVAS_H / rect.height;
    const cx = (e.clientX ?? e.touches?.[0]?.clientX ?? 0) - rect.left;
    const cy = (e.clientY ?? e.touches?.[0]?.clientY ?? 0) - rect.top;
    return { x: cx * scaleX, y: cy * scaleY };
  }

  function startDrag(e, item, mode = "move") {
    e.preventDefault();
    e.stopPropagation();
    setSelectedId(item._k);
    const p = getCanvasPoint(e);
    dragRef.current = {
      mode,
      key: item._k,
      startX: p.x,
      startY: p.y,
      orig: { ...item },
    };
    window.addEventListener("mousemove", onDrag);
    window.addEventListener("mouseup", endDrag);
    window.addEventListener("touchmove", onDrag, { passive: false });
    window.addEventListener("touchend", endDrag);
  }

  function onDrag(e) {
    e.preventDefault?.();
    const d = dragRef.current;
    if (!d) return;
    const p = getCanvasPoint(e);
    const dx = p.x - d.startX;
    const dy = p.y - d.startY;
    setItems((prev) =>
      prev.map((i) => {
        if (i._k !== d.key) return i;
        if (d.mode === "move") {
          return { ...i, x: snap(d.orig.x + dx), y: snap(d.orig.y + dy) };
        }
        if (d.mode === "resize") {
          const nw = Math.max(60, snap(d.orig.w + dx));
          const nh = Math.max(60, snap(d.orig.h + dy));
          return { ...i, w: nw, h: nh };
        }
        if (d.mode === "rotate") {
          const cx = d.orig.x + d.orig.w / 2;
          const cy = d.orig.y + d.orig.h / 2;
          const angle =
            (Math.atan2(p.y - cy, p.x - cx) * 180) / Math.PI + 90;
          return { ...i, rotation: angle };
        }
        return i;
      })
    );
  }

  function endDrag() {
    if (dragRef.current) markDirty();
    dragRef.current = null;
    window.removeEventListener("mousemove", onDrag);
    window.removeEventListener("mouseup", endDrag);
    window.removeEventListener("touchmove", onDrag);
    window.removeEventListener("touchend", endDrag);
  }

  async function handleSave() {
    setSaving(true);
    try {
      const cleanItems = items.map(({ _k, ...rest }) => rest);
      const res = await updateMoodboard(moodboardId, {
        name,
        items: cleanItems,
        background,
      });
      setMoodboard(res);
      setDirty(false);
      toast.success("Moodboard saved");
    } catch {
      toast.error("Failed to save");
    } finally {
      setSaving(false);
    }
  }

  // -------- Canvas drop (files + external URLs) --------
  function hasUsableDrag(e) {
    const t = e.dataTransfer?.types;
    if (!t) return false;
    return (
      t.includes("Files") ||
      t.includes("text/uri-list") ||
      t.includes("text/plain") ||
      t.includes("text/html")
    );
  }
  function onCanvasDragEnter(e) {
    if (!hasUsableDrag(e)) return;
    e.preventDefault();
    dropCounter.current += 1;
    setDropOver(true);
  }
  function onCanvasDragOver(e) {
    if (!hasUsableDrag(e)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "copy";
  }
  function onCanvasDragLeave() {
    dropCounter.current = Math.max(0, dropCounter.current - 1);
    if (dropCounter.current === 0) setDropOver(false);
  }
  async function onCanvasDrop(e) {
    e.preventDefault();
    dropCounter.current = 0;
    setDropOver(false);
    const dt = e.dataTransfer;
    if (!dt) return;
    const point = getCanvasPoint(e);
    const files = Array.from(dt.files || []);
    if (files.length > 0) {
      await uploadAndPlace(files, point);
      return;
    }
    const urls = extractUrlsFromDT(dt);
    if (urls.length > 0) {
      await importUrlsAndPlace(urls, point);
    }
  }

  async function uploadAndPlace(files, point) {
    setDropBusy(true);
    let offset = 0;
    for (const f of files) {
      const isFilePdf =
        f.type === "application/pdf" || /\.pdf$/i.test(f.name);
      const isImage = (f.type || "").startsWith("image/");
      if (!isFilePdf && !isImage) {
        toast.error(`Unsupported: ${f.name}`);
        continue;
      }
      try {
        const raw = await readFileAsDataURL(f);
        const data = isFilePdf ? raw : await downsizeImage(raw, 1600, 0.85);
        const created = await uploadImage(projectId, {
          project_id: projectId,
          data,
          filename: f.name,
          category: isFilePdf ? "inspiration" : "inspiration",
          mime: isFilePdf ? "application/pdf" : f.type || "image/jpeg",
        });
        setImages((prev) => [created, ...prev]);
        addToCanvas(created, { x: point.x + offset, y: point.y + offset });
        offset += 40;
        if (!isFilePdf) {
          analyzeImage({ image_data: data, image_id: created.id })
            .then((res) => {
              setImages((prev) =>
                prev.map((i) =>
                  i.id === created.id ? { ...i, ...res, ai_analyzed: true } : i
                )
              );
            })
            .catch(() => {});
        }
      } catch (e) {
        console.error(e);
        toast.error(`Failed: ${f.name}`);
      }
    }
    setDropBusy(false);
    toast.success(`${files.length} added to board`);
  }

  async function importUrlsAndPlace(urls, point) {
    setDropBusy(true);
    let offset = 0;
    for (const u of urls) {
      try {
        const created = await importFromUrl({
          project_id: projectId,
          url: u,
          category: "inspiration",
        });
        setImages((prev) => [created, ...prev]);
        addToCanvas(created, { x: point.x + offset, y: point.y + offset });
        offset += 40;
        if (created?.mime?.startsWith("image/")) {
          analyzeImage({ image_data: created.data, image_id: created.id })
            .then((res) => {
              setImages((prev) =>
                prev.map((i) =>
                  i.id === created.id ? { ...i, ...res, ai_analyzed: true } : i
                )
              );
            })
            .catch(() => {});
        }
      } catch (e) {
        console.error(e);
        toast.error(`URL import failed`);
      }
    }
    setDropBusy(false);
  }

  async function importUrlSubmit(e) {
    e.preventDefault();
    if (!urlValue.trim()) return;
    setUrlBusy(true);
    try {
      const created = await importFromUrl({
        project_id: projectId,
        url: urlValue.trim(),
        category: "inspiration",
      });
      setImages((prev) => [created, ...prev]);
      addToCanvas(created);
      toast.success("Imported and placed on board");
      if (created?.mime?.startsWith("image/")) {
        analyzeImage({ image_data: created.data, image_id: created.id })
          .then((res) => {
            setImages((prev) =>
              prev.map((i) =>
                i.id === created.id ? { ...i, ...res, ai_analyzed: true } : i
              )
            );
          })
          .catch(() => {});
      }
      setUrlValue("");
      setUrlImportOpen(false);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Failed to import URL";
      toast.error(typeof msg === "string" ? msg : "Failed to import URL");
    } finally {
      setUrlBusy(false);
    }
  }

  async function snapshotCanvas() {
    const node = canvasRef.current?.parentElement;
    if (!node) throw new Error("Canvas not available");
    // Briefly hide selection outline
    const prevSel = selectedId;
    setSelectedId(null);
    await new Promise((r) => setTimeout(r, 60));
    const canvas = await html2canvas(node, {
      backgroundColor: background,
      scale: 2,
      useCORS: true,
      logging: false,
    });
    setSelectedId(prevSel);
    return canvas;
  }

  async function exportPNG() {
    if (exporting) return;
    setExporting(true);
    try {
      const canvas = await snapshotCanvas();
      const dataUrl = canvas.toDataURL("image/png");
      const a = document.createElement("a");
      a.href = dataUrl;
      a.download = `${(name || "moodboard").replace(/[^\w-]+/g, "_")}.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      toast.success("PNG exported");
    } catch (e) {
      toast.error("Export failed");
    } finally {
      setExporting(false);
    }
  }

  async function exportPDF(sections) {
    if (exporting) return;
    setExporting(true);
    try {
      const includeBoard = sections.includes("board");
      const includeTitle = sections.includes("title");
      const includeProject = sections.includes("project");
      const includeItems = sections.includes("items");

      const pdf = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      const margin = 10;

      // Header
      let yCursor = margin;
      if (includeTitle) {
        pdf.setFont("helvetica", "normal");
        pdf.setFontSize(16);
        pdf.text(name || "Moodboard", margin, margin + 6);
        yCursor = margin + 6;
      }
      if (includeProject && project?.name) {
        pdf.setFontSize(10);
        pdf.setTextColor(120);
        pdf.text(project.name, margin, yCursor + 6);
        pdf.setTextColor(0);
        yCursor += 6;
      }

      // Board snapshot
      if (includeBoard) {
        const canvas = await snapshotCanvas();
        const imgData = canvas.toDataURL("image/jpeg", 0.92);
        const top = yCursor + 6;
        const availW = pageW - margin * 2;
        const availH = pageH - top - margin;
        const ratio = canvas.width / canvas.height;
        let w = availW;
        let h = w / ratio;
        if (h > availH) {
          h = availH;
          w = h * ratio;
        }
        const x = margin + (availW - w) / 2;
        pdf.addImage(imgData, "JPEG", x, top, w, h);
      }

      // Items list on new page
      if (includeItems && items.length > 0) {
        pdf.addPage("a4", "landscape");
        pdf.setFontSize(14);
        pdf.text("Items on board", margin, margin + 6);
        pdf.setFontSize(10);
        let y = margin + 14;
        items.forEach((it, idx) => {
          const img = imageById[it.image_id];
          const label = img?.title || img?.filename || `Image ${idx + 1}`;
          const tags = (img?.tags || []).slice(0, 4).join(", ");
          const line = `${idx + 1}. ${label}${tags ? "  —  " + tags : ""}`;
          if (y > pageH - margin) {
            pdf.addPage("a4", "landscape");
            y = margin + 10;
          }
          pdf.text(line, margin, y);
          y += 6;
        });
      }

      pdf.save(`${(name || "moodboard").replace(/[^\w-]+/g, "_")}.pdf`);
      toast.success("PDF exported");
    } catch (e) {
      toast.error("PDF export failed");
    } finally {
      setExporting(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-32 text-stone-400">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
      </div>
    );
  }
  if (!moodboard || !project) {
    return (
      <div className="max-w-xl mx-auto px-6 py-20 text-center">
        <p className="text-stone-500">Moodboard not found.</p>
        <Link to="/studio" className="text-stone-900 underline mt-3 inline-block">
          Back to studio
        </Link>
      </div>
    );
  }

  return (
    <div
      className="max-w-[1800px] mx-auto px-4 md:px-8 py-6"
      data-testid="moodboard-editor"
    >
      {/* Top bar */}
      <div className="flex items-center justify-between gap-4 flex-wrap mb-5">
        <div className="flex items-center gap-3 min-w-0">
          <button
            onClick={async () => {
              if (dirty) {
                const ok = await confirm({
                  title: "Discard unsaved changes?",
                  description:
                    "Your latest edits to this moodboard haven't been saved yet.",
                  confirmLabel: "Discard changes",
                  destructive: true,
                });
                if (!ok) return;
              }
              nav(`/project/${projectId}`);
            }}
            data-testid="back-to-workspace"
            className="text-stone-500 hover:text-stone-900 inline-flex items-center gap-2 text-sm shrink-0"
          >
            <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
            {project.name}
          </button>
          <span className="text-stone-300">/</span>
          <Input
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              markDirty();
            }}
            data-testid="moodboard-title-input"
            className="font-display text-xl border-none shadow-none focus-visible:ring-0 px-0 h-auto bg-transparent w-auto min-w-[180px]"
          />
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <input
            ref={deviceInputRef}
            type="file"
            accept="image/*,application/pdf"
            multiple
            className="hidden"
            data-testid="moodboard-device-input"
            onChange={async (e) => {
              const files = Array.from(e.target.files || []);
              e.target.value = "";
              if (files.length === 0) return;
              const rect = canvasRef.current?.getBoundingClientRect();
              const center = rect
                ? { x: CANVAS_W / 2, y: CANVAS_H / 2 }
                : { x: CANVAS_W / 2, y: CANVAS_H / 2 };
              await uploadAndPlace(files, center);
            }}
          />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                data-testid="moodboard-add-image"
                className="rounded-full"
              >
                <Plus className="w-4 h-4 mr-2" strokeWidth={1.5} />
                Add image
                <ChevronDown className="w-3.5 h-3.5 ml-1.5 opacity-60" strokeWidth={1.5} />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="min-w-[200px]">
              <DropdownMenuItem
                onClick={() => setPickerOpen(true)}
                data-testid="moodboard-add-image-from-library"
              >
                <Library className="w-4 h-4 mr-2" strokeWidth={1.5} />
                From library
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => deviceInputRef.current?.click()}
                data-testid="moodboard-add-image-from-device"
              >
                <Upload className="w-4 h-4 mr-2" strokeWidth={1.5} />
                From this device
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Dialog open={pickerOpen} onOpenChange={setPickerOpen}>
            <DialogContent className="max-w-4xl">
              <DialogHeader>
                <DialogTitle className="font-display text-2xl">
                  Pick from library
                </DialogTitle>
                <DialogDescription className="text-sm text-stone-500">
                  Click an image to add it to the canvas.
                </DialogDescription>
              </DialogHeader>
              {images.length === 0 ? (
                <p className="text-stone-500 py-8 text-center">
                  No images in this project yet.
                </p>
              ) : (
                <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 max-h-[65vh] overflow-y-auto pt-2">
                  {images.map((img) => {
                    const pdf = isPdf(img);
                    return (
                      <button
                        key={img.id}
                        data-testid={`moodboard-pick-${img.id}`}
                        onClick={() => {
                          addToCanvas(img);
                        }}
                        className="aspect-square rounded-lg overflow-hidden bg-stone-100 hover:ring-2 hover:ring-stone-900 transition-all relative group"
                      >
                        {pdf ? (
                          <PdfThumbnail src={img.data} label={img.filename || "PDF"} />
                        ) : (
                          <img src={img.data} alt="" className="w-full h-full object-cover" />
                        )}
                        <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/30 transition-colors flex items-center justify-center">
                          <Plus
                            className="w-6 h-6 text-white opacity-0 group-hover:opacity-100"
                            strokeWidth={1.5}
                          />
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
              <div className="flex justify-end pt-2">
                <Button
                  onClick={() => setPickerOpen(false)}
                  className="rounded-full bg-stone-900 text-white hover:bg-stone-800"
                  data-testid="moodboard-picker-done"
                >
                  <Check className="w-4 h-4 mr-2" strokeWidth={1.5} />
                  Done
                </Button>
              </div>
            </DialogContent>
          </Dialog>
          <Button
            variant="outline"
            onClick={() => addTextBlock()}
            data-testid="moodboard-add-text"
            className="rounded-full"
          >
            <Type className="w-4 h-4 mr-2" strokeWidth={1.5} />
            Add text
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                data-testid="moodboard-add-shape"
                className="rounded-full"
              >
                <Shapes className="w-4 h-4 mr-2" strokeWidth={1.5} />
                Add shape
                <ChevronDown className="w-3.5 h-3.5 ml-1.5 opacity-60" strokeWidth={1.5} />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="min-w-[180px]">
              <DropdownMenuItem
                onClick={() => addShape("rectangle")}
                data-testid="moodboard-add-shape-rectangle"
              >
                <Square className="w-4 h-4 mr-2" strokeWidth={1.5} />
                Rectangle
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => addShape("ellipse")}
                data-testid="moodboard-add-shape-ellipse"
              >
                <Circle className="w-4 h-4 mr-2" strokeWidth={1.5} />
                Ellipse
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => addShape("triangle")}
                data-testid="moodboard-add-shape-triangle"
              >
                <Triangle className="w-4 h-4 mr-2" strokeWidth={1.5} />
                Triangle
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => addShape("line")}
                data-testid="moodboard-add-shape-line"
              >
                <Minus className="w-4 h-4 mr-2" strokeWidth={1.5} />
                Line
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Dialog open={urlImportOpen} onOpenChange={setUrlImportOpen}>
            <DialogTrigger asChild>
              <Button
                variant="outline"
                data-testid="moodboard-add-url"
                className="rounded-full"
              >
                <Link2 className="w-4 h-4 mr-2" strokeWidth={1.5} />
                From URL
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle className="font-display text-2xl">Import from URL</DialogTitle>
                <DialogDescription className="text-sm text-stone-500">
                  Paste a direct image URL, a PDF URL, or a Pinterest pin link.
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={importUrlSubmit} className="space-y-4 mt-2">
                <Input
                  value={urlValue}
                  onChange={(e) => setUrlValue(e.target.value)}
                  placeholder="https://pinterest.com/pin/... or .jpg/.pdf URL"
                  data-testid="moodboard-url-input"
                  autoFocus
                  required
                />
                <div className="flex justify-end gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setUrlImportOpen(false)}
                    className="rounded-full"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={urlBusy}
                    data-testid="moodboard-url-submit"
                    className="rounded-full bg-stone-900 text-white hover:bg-stone-800"
                  >
                    {urlBusy ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Link2 className="w-4 h-4 mr-2" strokeWidth={1.5} />
                    )}
                    Import & place
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
          <Button
            variant="outline"
            onClick={exportPNG}
            disabled={exporting}
            data-testid="moodboard-export-png"
            className="rounded-full"
          >
            {exporting ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Download className="w-4 h-4 mr-2" strokeWidth={1.5} />
            )}
            PNG
          </Button>
          <Button
            variant="outline"
            onClick={() => setPdfDialogOpen(true)}
            disabled={exporting}
            data-testid="moodboard-export-pdf"
            className="rounded-full"
          >
            {exporting ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <FileText className="w-4 h-4 mr-2" strokeWidth={1.5} />
            )}
            PDF
          </Button>
          <Button
            variant="outline"
            onClick={() => setShareOpen(true)}
            data-testid="moodboard-share-button"
            className="rounded-full"
          >
            <Share2 className="w-4 h-4 mr-2" strokeWidth={1.5} />
            Share
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving || !dirty}
            data-testid="moodboard-save-button"
            className="rounded-full bg-stone-900 text-white hover:bg-stone-800"
          >
            {saving ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Save className="w-4 h-4 mr-2" strokeWidth={1.5} />
            )}
            {dirty ? "Save" : "Saved"}
          </Button>
        </div>
      </div>

      {/* Sub toolbar */}
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div className="flex items-center gap-2" data-testid="bg-swatches">
          <span className="text-xs uppercase tracking-[0.15em] text-stone-500 font-semibold mr-1">
            Background
          </span>
          {BG_SWATCHES.map((c) => (
            <button
              key={c}
              onClick={() => {
                setBackground(c);
                markDirty();
              }}
              data-testid={`bg-${c.replace("#", "")}`}
              className={`w-7 h-7 rounded-full border transition-transform ${
                background === c
                  ? "ring-2 ring-stone-900 scale-110"
                  : "border-stone-200 hover:scale-105"
              }`}
              style={{ background: c }}
              aria-label={`background ${c}`}
            />
          ))}
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant={snapEnabled ? "default" : "outline"}
            size="sm"
            onClick={() => setSnapEnabled((v) => !v)}
            data-testid="snap-toggle-button"
            className={`rounded-full ${
              snapEnabled
                ? "bg-stone-900 text-white hover:bg-stone-800"
                : ""
            }`}
            title={snapEnabled ? "Snap: ON (hold Shift to bypass)" : "Snap: OFF"}
          >
            <Grid3x3 className="w-4 h-4" strokeWidth={1.5} />
            <span className="hidden sm:inline ml-1 text-xs">
              Snap {snapEnabled ? "on" : "off"}
            </span>
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => rotateSelected(-15)}
            disabled={!selectedId}
            data-testid="rotate-left-button"
            className="rounded-full"
          >
            <RotateCw className="w-4 h-4 -scale-x-100" strokeWidth={1.5} />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => rotateSelected(15)}
            disabled={!selectedId}
            data-testid="rotate-right-button"
            className="rounded-full"
          >
            <RotateCw className="w-4 h-4" strokeWidth={1.5} />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={bringForward}
            disabled={!selectedId}
            data-testid="bring-forward-button"
            className="rounded-full"
          >
            <Layers className="w-4 h-4" strokeWidth={1.5} />
            <span className="hidden sm:inline ml-1 text-xs">Forward</span>
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={sendBackward}
            disabled={!selectedId}
            data-testid="send-backward-button"
            className="rounded-full"
          >
            <Layers className="w-4 h-4 -scale-y-100" strokeWidth={1.5} />
            <span className="hidden sm:inline ml-1 text-xs">Backward</span>
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              if (selectedItem?.kind === "text") setTextStyleOpen(true);
              else if (selectedItem?.kind === "shape") setShapeStyleOpen(true);
            }}
            disabled={
              !selectedItem ||
              (selectedItem.kind !== "text" && selectedItem.kind !== "shape")
            }
            data-testid="style-button"
            className="rounded-full"
            title={
              selectedItem?.kind === "shape"
                ? "Shape style"
                : "Text style"
            }
          >
            {selectedItem?.kind === "shape" ? (
              <Palette className="w-4 h-4" strokeWidth={1.5} />
            ) : (
              <SlidersHorizontal className="w-4 h-4" strokeWidth={1.5} />
            )}
            <span className="hidden sm:inline ml-1 text-xs">Style</span>
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={removeSelected}
            disabled={!selectedId}
            data-testid="remove-item-button"
            className="rounded-full text-red-600 hover:text-red-700"
          >
            <Trash2 className="w-4 h-4" strokeWidth={1.5} />
          </Button>
        </div>
      </div>

      {/* Canvas */}
      <div
        className="relative w-full overflow-hidden rounded-2xl border border-stone-200 dotted-canvas"
        style={{ aspectRatio: `${CANVAS_W} / ${CANVAS_H}`, background }}
        onMouseDown={() => setSelectedId(null)}
        onTouchStart={() => setSelectedId(null)}
        onDragEnter={onCanvasDragEnter}
        onDragOver={onCanvasDragOver}
        onDragLeave={onCanvasDragLeave}
        onDrop={onCanvasDrop}
        data-testid="moodboard-canvas"
      >
        <div ref={canvasRef} className="absolute inset-0">
          {items
            .slice()
            .sort((a, b) => (a.z || 0) - (b.z || 0))
            .map((it) => {
              const isSel = selectedId === it._k;
              const commonStyle = {
                left: `${(it.x / CANVAS_W) * 100}%`,
                top: `${(it.y / CANVAS_H) * 100}%`,
                width: `${(it.w / CANVAS_W) * 100}%`,
                height: `${(it.h / CANVAS_H) * 100}%`,
                transform: `rotate(${it.rotation || 0}deg)`,
                zIndex: it.z || 1,
              };

              // ---- TEXT item ----
              if (it.kind === "text") {
                const editingThis = editingTextK === it._k;
                return (
                  <div
                    key={it._k}
                    data-testid={`moodboard-item-${it._k}`}
                    onMouseDown={(e) => {
                      // Don't start a drag while inline-editing — let the user
                      // place their caret / select within the text.
                      if (editingThis) {
                        e.stopPropagation();
                        return;
                      }
                      startDrag(e, it, "move");
                    }}
                    onTouchStart={(e) => {
                      if (editingThis) {
                        e.stopPropagation();
                        return;
                      }
                      startDrag(e, it, "move");
                    }}
                    onDoubleClick={(e) => {
                      e.stopPropagation();
                      // Select the item so the rich style toolbar appears,
                      // and switch its text node into contentEditable.
                      setSelectedId(it._k);
                      setEditingTextK(it._k);
                    }}
                    className={`absolute select-none ${
                      editingThis ? "cursor-text" : "cursor-move"
                    }`}
                    style={commonStyle}
                  >
                    {editingThis ? (
                      <textarea
                        autoFocus
                        defaultValue={it.text || ""}
                        data-testid={`moodboard-text-${it._k}`}
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={(e) => e.stopPropagation()}
                        onBlur={(e) => {
                          const newText = e.currentTarget.value;
                          setItems((prev) =>
                            prev.map((x) =>
                              x._k === it._k ? { ...x, text: newText } : x,
                            ),
                          );
                          markDirty();
                          setEditingTextK(null);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Escape") {
                            e.preventDefault();
                            e.currentTarget.blur();
                          }
                        }}
                        className="w-full h-full bg-white/85 ring-2 ring-stone-900 ring-offset-2 ring-offset-transparent rounded-sm focus:outline-none px-2 resize-none border-none"
                        style={{
                          fontFamily:
                            FONT_FAMILIES[it.font_family] ||
                            FONT_FAMILIES.display,
                          fontSize: `${(it.font_size || 32) * 0.6}px`,
                          fontWeight: it.font_weight || 400,
                          fontStyle: it.font_style || "normal",
                          textDecoration: it.text_decoration || "none",
                          textTransform: it.text_transform || "none",
                          lineHeight:
                            it.line_height !== undefined
                              ? it.line_height
                              : 1.15,
                          letterSpacing: `${(it.letter_spacing || 0)}em`,
                          color: it.color || "#1c1b17",
                          textAlign: it.align || "left",
                        }}
                      />
                    ) : (
                      <div
                        data-testid={`moodboard-text-${it._k}`}
                        className={`w-full h-full flex items-center px-2 whitespace-pre-wrap break-words ${
                          isSel
                            ? "outline outline-2 outline-stone-900 outline-offset-2"
                            : ""
                        }`}
                        style={{
                          fontFamily:
                            FONT_FAMILIES[it.font_family] ||
                            FONT_FAMILIES.display,
                          fontSize: `${(it.font_size || 32) * 0.6}px`,
                          fontWeight: it.font_weight || 400,
                          fontStyle: it.font_style || "normal",
                          textDecoration: it.text_decoration || "none",
                          textTransform: it.text_transform || "none",
                          lineHeight:
                            it.line_height !== undefined
                              ? it.line_height
                              : 1.15,
                          letterSpacing: `${(it.letter_spacing || 0)}em`,
                          color: it.color || "#1c1b17",
                          textAlign: it.align || "left",
                          justifyContent:
                            it.align === "center"
                              ? "center"
                              : it.align === "right"
                              ? "flex-end"
                              : "flex-start",
                        }}
                      >
                        {it.text || ""}
                      </div>
                    )}
                    {isSel && (
                      <>
                        <div
                          onMouseDown={(e) => startDrag(e, it, "resize")}
                          onTouchStart={(e) => startDrag(e, it, "resize")}
                          data-testid="resize-handle"
                          className="absolute -right-2 -bottom-2 w-5 h-5 rounded-full bg-stone-900 border-2 border-white cursor-nwse-resize"
                        />
                        <div
                          onMouseDown={(e) => startDrag(e, it, "rotate")}
                          onTouchStart={(e) => startDrag(e, it, "rotate")}
                          data-testid="rotate-handle"
                          className="absolute left-1/2 -top-7 -translate-x-1/2 w-6 h-6 rounded-full bg-white border-2 border-stone-900 cursor-grab flex items-center justify-center"
                        >
                          <RotateCw className="w-3 h-3 text-stone-900" strokeWidth={2} />
                        </div>
                        <button
                          type="button"
                          onMouseDown={(e) => e.stopPropagation()}
                          onClick={(e) => {
                            e.stopPropagation();
                            setItems((prev) =>
                              prev.filter((x) => x._k !== it._k)
                            );
                            setSelectedId(null);
                            markDirty();
                          }}
                          data-testid="quick-remove"
                          className="absolute -left-2 -top-2 w-6 h-6 rounded-full bg-white border-2 border-stone-900 flex items-center justify-center"
                        >
                          <X className="w-3 h-3 text-stone-900" strokeWidth={2} />
                        </button>
                      </>
                    )}
                  </div>
                );
              }

              // ---- SHAPE item ----
              if (it.kind === "shape") {
                return (
                  <div
                    key={it._k}
                    data-testid={`moodboard-item-${it._k}`}
                    onMouseDown={(e) => startDrag(e, it, "move")}
                    onTouchStart={(e) => startDrag(e, it, "move")}
                    onDoubleClick={(e) => {
                      e.stopPropagation();
                      setSelectedId(it._k);
                      setShapeStyleOpen(true);
                    }}
                    className={`absolute select-none cursor-move ${
                      isSel ? "outline outline-2 outline-stone-900 outline-offset-2 rounded-sm" : ""
                    }`}
                    style={commonStyle}
                  >
                    <MoodboardShape item={it} />
                    {isSel && (
                      <>
                        <div
                          onMouseDown={(e) => startDrag(e, it, "resize")}
                          onTouchStart={(e) => startDrag(e, it, "resize")}
                          data-testid="resize-handle"
                          className="absolute -right-2 -bottom-2 w-5 h-5 rounded-full bg-stone-900 border-2 border-white cursor-nwse-resize"
                        />
                        <div
                          onMouseDown={(e) => startDrag(e, it, "rotate")}
                          onTouchStart={(e) => startDrag(e, it, "rotate")}
                          data-testid="rotate-handle"
                          className="absolute left-1/2 -top-7 -translate-x-1/2 w-6 h-6 rounded-full bg-white border-2 border-stone-900 cursor-grab flex items-center justify-center"
                        >
                          <RotateCw className="w-3 h-3 text-stone-900" strokeWidth={2} />
                        </div>
                        <button
                          type="button"
                          onMouseDown={(e) => e.stopPropagation()}
                          onClick={(e) => {
                            e.stopPropagation();
                            setItems((prev) =>
                              prev.filter((x) => x._k !== it._k)
                            );
                            setSelectedId(null);
                            markDirty();
                          }}
                          data-testid="quick-remove"
                          className="absolute -left-2 -top-2 w-6 h-6 rounded-full bg-white border-2 border-stone-900 flex items-center justify-center"
                        >
                          <X className="w-3 h-3 text-stone-900" strokeWidth={2} />
                        </button>
                      </>
                    )}
                  </div>
                );
              }

              // ---- IMAGE / PDF item ----
              const img = imageById[it.image_id];
              if (!img) return null;
              const itemIsPdf = isPdf(img);
              return (
                <div
                  key={it._k}
                  data-testid={`moodboard-item-${it._k}`}
                  onMouseDown={(e) => startDrag(e, it, "move")}
                  onTouchStart={(e) => startDrag(e, it, "move")}
                  className="absolute select-none cursor-move"
                  style={commonStyle}
                >
                  <div
                    className={`w-full h-full rounded-md shadow-md bg-white overflow-hidden ${
                      isSel ? "outline outline-2 outline-stone-900" : ""
                    }`}
                  >
                    {itemIsPdf ? (
                      <PdfThumbnail src={img.data} label={img.filename || "PDF"} />
                    ) : (
                      <img
                        src={img.data}
                        alt=""
                        draggable={false}
                        className="w-full h-full object-cover"
                      />
                    )}
                  </div>
                  {isSel && (
                    <>
                      <div
                        onMouseDown={(e) => startDrag(e, it, "resize")}
                        onTouchStart={(e) => startDrag(e, it, "resize")}
                        data-testid="resize-handle"
                        className="absolute -right-2 -bottom-2 w-5 h-5 rounded-full bg-stone-900 border-2 border-white cursor-nwse-resize"
                      />
                      <div
                        onMouseDown={(e) => startDrag(e, it, "rotate")}
                        onTouchStart={(e) => startDrag(e, it, "rotate")}
                        data-testid="rotate-handle"
                        className="absolute left-1/2 -top-7 -translate-x-1/2 w-6 h-6 rounded-full bg-white border-2 border-stone-900 cursor-grab flex items-center justify-center"
                      >
                        <RotateCw className="w-3 h-3 text-stone-900" strokeWidth={2} />
                      </div>
                      <button
                        type="button"
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={(e) => {
                          e.stopPropagation();
                          setItems((prev) =>
                            prev.filter((x) => x._k !== it._k)
                          );
                          setSelectedId(null);
                          markDirty();
                        }}
                        data-testid="quick-remove"
                        className="absolute -left-2 -top-2 w-6 h-6 rounded-full bg-white border-2 border-stone-900 flex items-center justify-center"
                      >
                        <X className="w-3 h-3 text-stone-900" strokeWidth={2} />
                      </button>
                    </>
                  )}
                </div>
              );
            })}
        </div>
        {items.length === 0 && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <p className="text-stone-400 text-sm">
              Empty canvas — drop files, add text, add a shape, or click "Add image" to begin.
            </p>
          </div>
        )}
        {(dropOver || dropBusy) && (
          <div
            className="absolute inset-0 z-40 flex items-center justify-center bg-stone-900/40 backdrop-blur-sm pointer-events-none"
            data-testid="moodboard-drop-overlay"
          >
            <div className="bg-white rounded-2xl shadow-2xl px-8 py-5 flex items-center gap-3">
              {dropBusy ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin text-stone-900" />
                  <span className="font-display text-xl text-stone-900">Adding…</span>
                </>
              ) : (
                <>
                  <Plus className="w-5 h-5 text-stone-900" />
                  <span className="font-display text-xl text-stone-900">
                    Drop to place on board
                  </span>
                </>
              )}
            </div>
          </div>
        )}
      </div>

      <p className="text-xs text-stone-400 mt-3">
        Tap to select. Drag to move. Use handles at bottom-right to resize, top to rotate.
        Double-click text to edit content. Click "Style" to open the style dialog.
        {snapEnabled && " Hold Shift to bypass snap-to-grid."}
        {" "}{items.length} item{items.length === 1 ? "" : "s"} on board.
      </p>

      <ExportChecklistDialog
        open={pdfDialogOpen}
        onOpenChange={setPdfDialogOpen}
        title="Export Moodboard PDF"
        sections={[
          { key: "title", label: "Moodboard title", hint: "Header line at the top", defaultChecked: true },
          { key: "project", label: "Project name", hint: "Small subtitle under the title", defaultChecked: true },
          { key: "board", label: "Board snapshot", hint: "Full-page render of the canvas", defaultChecked: true },
          { key: "items", label: "Items list page", hint: "Captions + tags appendix", defaultChecked: true },
        ]}
        onExport={exportPDF}
      />

      <ShareDialog
        open={shareOpen}
        onOpenChange={setShareOpen}
        type="moodboard"
        targetId={moodboardId}
        label="moodboard"
      />

      <MoodboardTextStyleDialog
        open={textStyleOpen}
        onOpenChange={setTextStyleOpen}
        item={selectedItem}
        onChange={updateSelectedItem}
      />

      <MoodboardShapeStyleDialog
        open={shapeStyleOpen}
        onOpenChange={setShapeStyleOpen}
        item={selectedItem}
        onChange={updateSelectedItem}
      />
    </div>
  );
}
